import { useState, useMemo, useEffect } from "react";
import { useDispatch } from "react-redux";
import Badge from "react-bootstrap/Badge";
import Button from "react-bootstrap/Button";
import { useLocation, useNavigate } from "react-router-dom";
import cloneDeep from "lodash/cloneDeep";
import isEqual from "lodash/isEqual";
import { resources } from "@constants/hardcoded-strings";
import { IDENTITY_PROVIDERS_URL } from "@constants/routes";
import { ButtonTypes } from "@enums/button-types";
import { LabelWidthTypes } from "@enums/label-width-types";
import { ModalTypes } from "@enums/modal-types";
import { ChangeRequestStatusTypes } from "@enums/change-request-status-types";
import { useDiscardChangesModal } from "@hooks/use-discard-changes-modal";
import {
    FormattedIdentityProviderDetails,
    IdentityProviderChangeRequestResponse
} from "@idw-types/data/identity-providers";
import { updateIdentityProviderReduxRequest } from "@redux-requests/identity-provider-details";
import { navigateToUtil } from "@utils/navigation-util";
import { getIdentityProviderValidationErrors } from "@utils/validation/identity-provider/identity-provider-validation-util";
import { ButtonWithSpinner } from "../common/button-with-spinner/button-with-spinner";
import { IdentityProviderDetailsDropdownMenu } from "@containers/identity-provider-details/identity-provider-details-dropdown-menu/identity-provider-details-dropdown-menu";
import { IdentityProviderDetailsTabContainer } from "@containers/identity-provider-details/identity-provider-details-tab-container/identity-provider-details-details-tab-container";
import { IdentityProviderDetailsReferenceData } from "@idw-types/state/identity-provider-details-state";
import styles from "./identity-provider-details-component.less";

type IdentityProviderDetailsComponentProps = {
    identityProviderDetails: FormattedIdentityProviderDetails;
    identityProviderChangeRequests: Array<IdentityProviderChangeRequestResponse>;
    identityProviderReferenceData: IdentityProviderDetailsReferenceData;
    setIdentityProviderChangeRequest: (changeRequest: IdentityProviderChangeRequestResponse) => void;
};

export const IdentityProviderDetailsComponent = (props: IdentityProviderDetailsComponentProps) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const routerLocation = useLocation();

    const {
        identityProviderDetails,
        identityProviderChangeRequests,
        identityProviderReferenceData,
        setIdentityProviderChangeRequest
    } = props;

    const { categories, classifications, companies, loas, protocols, schemes } = identityProviderReferenceData;

    const [labelWidth] = useState(LabelWidthTypes.MEDIUM);
    const [requestInProgress, setRequestInProgress] = useState(false);
    const [identityProviderDetailsState, setIdentityProviderDetailsState] = useState<FormattedIdentityProviderDetails>(identityProviderDetails);

    useEffect(() => {
        setIdentityProviderDetailsState(cloneDeep(identityProviderDetails));
    }, [identityProviderDetails]);

    const stateIsUnchanged = useMemo(() => {
        return isEqual(identityProviderDetails, identityProviderDetailsState);
    }, [identityProviderDetails, identityProviderDetailsState]);

    const hasUnsubmittedChangeRequest = useMemo(() => {
        if (identityProviderChangeRequests.length > 0) {
            return identityProviderChangeRequests[0].changeRequestStatus === ChangeRequestStatusTypes.UNSUBMITTED;
        }
        return false;
    }, [identityProviderChangeRequests]);

    const isUIDisabled = useMemo(() => {
        return !identityProviderDetails.permissions.canModify || !hasUnsubmittedChangeRequest || requestInProgress;
    }, [identityProviderDetails.permissions.canModify, hasUnsubmittedChangeRequest, requestInProgress]);

    useDiscardChangesModal({
        modalIdPrefix: "discard-identity-provider-changes",
        modalType: ModalTypes.DiscardIdentityProviderChanges,
        stateIsUnchanged
    });

    const onCloseClick = () => {
        navigateToUtil(IDENTITY_PROVIDERS_URL, navigate, routerLocation.pathname);
    };

    const onClickSave = async () => {
        setRequestInProgress(true);
        await updateIdentityProviderReduxRequest(dispatch)(
            identityProviderDetailsState,
            categories,
            classifications,
            companies,
            loas,
            protocols,
            schemes
        );
        setRequestInProgress(false);
    };

    const validationErrors = useMemo(() => {
        return getIdentityProviderValidationErrors(identityProviderDetailsState);
    }, [identityProviderDetailsState]);

    return (
        <div className={styles.identityProviderDetailsComponent}>
            <div className={styles.headerContainer}>
                <div className={styles.headerRow}>
                    <span className={styles.identityProviderName}>
                        {`${identityProviderDetailsState.name}`}
                    </span>
                    {identityProviderChangeRequests.length > 0 && (
                        <Badge pill bg={"secondary"}>{identityProviderChangeRequests[0].changeRequestStatus}</Badge>
                    )}
                    <div className={styles.buttonContainer}>
                        <Button
                            id="identity-provider-details-close-button"
                            onClick={onCloseClick}
                            variant={"outline-secondary"}
                        >
                            {resources.common.buttons.close.label}
                        </Button>
                        {hasUnsubmittedChangeRequest && identityProviderDetails.permissions.canModify && (
                            <ButtonWithSpinner
                                id="identity-provider-details-save-button"
                                disabled={requestInProgress || stateIsUnchanged || validationErrors.count > 0}
                                hideSpinner={stateIsUnchanged || validationErrors.count > 0}
                                variant="primary"
                                type={ButtonTypes.BUTTON}
                                onClick={onClickSave}
                                label={resources.common.buttons.save.label}
                            />
                        )}
                        <IdentityProviderDetailsDropdownMenu
                            stateIsUnchanged={stateIsUnchanged}
                            validationErrors={validationErrors}
                            identityProviderDetails={identityProviderDetails}
                            identityProviderChangeRequests={identityProviderChangeRequests}
                            requestInProgress={requestInProgress}
                            setIdentityProviderDetails={setIdentityProviderDetailsState}
                            setIdentityProviderChangeRequests={setIdentityProviderChangeRequest}
                            setRequestInProgress={setRequestInProgress}
                        />
                    </div>
                </div>
            </div>
            <IdentityProviderDetailsTabContainer
                labelWidth={labelWidth}
                isDisabled={isUIDisabled}
                identityProviderDetailsState={identityProviderDetailsState}
                identityProviderReferenceData={identityProviderReferenceData}
                validationErrors={validationErrors}
                setIdentityProviderDetailsState={setIdentityProviderDetailsState} />
        </div >
    );
};